/**
 * V113-7-3 — the system detail "Monitoring" tab (ux-adversary run v113-monitoring, round 4).
 *
 *   Class card     declared / inferred; Confirm (sends the inferred class) and Change (dialog); no class → picker.
 *   Plan strip     latest signed plan, "Out of date · Regenerate"; Sign disabled until the class is confirmed (409).
 *   Controls       AND cells per configuration item (auto / attested), the failing cause line, fix-first actions;
 *                  "Waiting for first run" after an Accept until a run lands; "N excluded" (v11.4 N/A).
 *   Candidates     Accept, or "Accept and set up" (pipeline step sheet) for a pipeline / library method; fit marker.
 *   Items          every configuration item with its state and one action (no dead ends).
 *   Bindings       one per control × item, ids behind Verify.  Timeline: what changed (30 days).
 * Reads: GET systems/:id/monitoring (+ timeline, monitoring-plan, plan packages, pack-validity). Writes: accept,
 * class, plan sign, method verify — gated client-side by monitoring-capabilities.ts; the server decides.
 */
import { useEffect, useId, useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, EmptyState, Field, Input, KpiStrip, KpiTile, Spinner, StatusGlyph } from '@aigovops/design-system';
import type { CoverageControl, SystemClass, TimelineEntry } from '@aigovops/core';
import {
  monitoringKeys, useAcceptCandidate, useDeclareSystemClass, useMonitoringCapabilities, useMonitoringPlanPackages,
  useMonitoringTimeline, usePackValidity, useSignMonitoringPlan, useSystemMonitoring,
} from '../../hooks/useMonitoring';
import { useReopenControl } from '../../hooks/useGaps';
import { useWorkspace } from '../../lib/workspace';
import { whoCanText, type Capability } from '../../lib/monitoring-capabilities';
import { monitoringErrorOf, verifyConfigurationItemMethod, type SystemMonitoringView } from '../../lib/monitoring-api';
import {
  askText, attestedLine, CLASS_ANCHOR, CLASS_CHOICES, ciHref, classLabel, classPhrase, controlAnchor, detectorLabel, envLabel, findingHref, metaLine, monitoringFindingsHref,
  fitLabel, kindLabel, carriedLine, CHANGES_ANCHOR, systemTabHref,
} from '../../lib/monitoring-view';
import { fmtDate, plural } from '../../lib/format';
import { ActionDialog, JumpLink, useAnnounce, useFocusAfter } from '../today/shared';
import { cadenceLabel, CauseLine, CopyField, GatedButton, Observation, PackChip, ProgressLines, StatusChip, useMemberNames, useRunAllChecks, VerifyDisclosure } from './parts';
import { GapRows } from './GapRows';
import '../../pages/today.css';
import '../../pages/program-screens.css';
import '../../pages/monitoring.css';

type View = SystemMonitoringView;
type CellState = 'pass' | 'fail' | 'unmon' | 'stale' | 'wait' | 'review' | 'gone';

const CTL_STATUS: Record<CoverageControl['reason'], { label: string; tone: 'ok' | 'red' | 'amber' | 'outline'; glyph: 'check' | 'x' | 'clock' | 'circle' }> = {
  covered: { label: 'Passing', tone: 'ok', glyph: 'check' },
  last_check_not_passing: { label: 'Stopped passing', tone: 'outline', glyph: 'x' },
  binding_not_fresh: { label: 'Past cadence', tone: 'amber', glyph: 'clock' },
  binding_missing: { label: 'No method', tone: 'outline', glyph: 'circle' },
  no_relevant_ci: { label: 'No method', tone: 'outline', glyph: 'circle' },
  unmonitored_ci: { label: 'Unmonitored', tone: 'outline', glyph: 'circle' },
};

function cellState(reason: string, waiting: boolean): CellState {
  if (reason === 'covered') return 'pass';
  if (reason === 'last_check_not_passing') return 'fail';
  if (reason === 'binding_not_fresh') return 'stale';
  if (reason === 'review_due') return 'review';
  if (reason === 'ci_gone') return 'gone';
  return waiting ? 'wait' : 'unmon';
}
const CELL_WORD: Record<CellState, string> = {
  pass: 'passing', fail: 'stopped passing', unmon: 'unmonitored', stale: 'past cadence', wait: 'waiting for first run', review: 'review due', gone: 'gone',
};
const CELL_GLYPH: Record<CellState, 'check' | 'x' | 'circle' | 'clock' | 'dots'> = { pass: 'check', fail: 'x', unmon: 'circle', stale: 'clock', wait: 'dots', review: 'clock', gone: 'circle' };

/** An accepted method whose binding has not run yet (Y-5). */
function waitingKeys(v: View): Set<string> {
  const ran = new Set(v.bindings.filter((b) => b.last_check_id).map((b) => `${b.control_id}|${b.component_id ?? ''}`));
  return new Set(v.acceptances.filter((a) => !a.retired_at && !ran.has(`${a.control_id}|${a.component_id ?? ''}`)).map((a) => `${a.control_id}|${a.component_id ?? ''}`));
}

export function SystemMonitoringTab({ slug, systemId }: { slug: string; systemId: string }) {
  const prefix = `/w/${slug}/app`;
  const q = useSystemMonitoring(systemId);
  const packages = useMonitoringPlanPackages(q.data?.class.current ? systemId : null);
  const validity = usePackValidity();
  const caps = useMonitoringCapabilities();
  const names = useMemberNames(slug);
  const [announcer, announce] = useAnnounce();
  const focusAfter = useFocusAfter();

  // Deep links (#mon-ctl-LS-4, #mon-class, #mon-plan) land with focus once the tab has rendered.
  const loaded = !!q.data;
  useEffect(() => {
    if (!loaded) return;
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (id.startsWith('mon-')) focusAfter(id);
  }, [loaded]); // eslint-disable-line react-hooks/exhaustive-deps

  if (q.loading) return <Spinner label="Loading monitoring" />;
  if (q.error || !q.data) {
    return <Alert variant="error">Monitoring could not be loaded. <Button compact onClick={() => void q.refetch()}>Retry</Button></Alert>;
  }
  const v = q.data;
  // Control titles come with the view (prescription[].title, bindings[].control_title).
  const titles = new Map<string, string>();
  for (const b of v.bindings) if (b.control_title) titles.set(b.control_id, b.control_title);
  for (const p of v.prescription) if (p.title) titles.set(p.control_id, p.title);
  const title = (id: string) => titles.get(id) ?? id;
  const latest = packages.data?.packages[0] ?? null;

  return (
    <div className="mon-page" data-part="system-monitoring">
      {announcer}
      <SystemKpis v={v} slug={slug} prefix={prefix} canDeclare={caps.declare_class.allowed} askDeclare={askText(caps.declare_class, { ownerName: v.facts.owner_name })} onAnnounce={announce} />
      {/* V114-5-1 (panel): the open gaps sit directly under the KPI strip — the tab's problems lead. */}
      {v.class.current ? <GapRows v={v} slug={slug} prefix={prefix} title={title} onAnnounce={announce} /> : null}
      {v.class.current ? (
        <PlanStrip slug={slug} systemId={systemId} classConfirmed={v.class.source === 'declared'} freshness={v.plan_freshness}
          latest={latest} canSign={caps.sign_plan} onAnnounce={announce} />
      ) : null}
      {!v.class.current ? (
        <ClassPicker slug={slug} v={v} canDeclare={caps.declare_class} ownerName={v.facts.owner_name} onDone={(m) => { announce(m); focusAfter('mon-controls'); }} />
      ) : (
        <Controls v={v} slug={slug} prefix={prefix} title={title} validityCause={(validity.data?.validity?.reasons ?? []).flatMap((r) => r.items).find((i) => i.system_id === systemId && i.cause)?.cause?.label ?? null}
          caps={caps} names={names} onAnnounce={announce} />
      )}
      {v.class.current ? <Candidates v={v} slug={slug} prefix={prefix} title={title} canAccept={caps.accept_candidate} ownerName={v.facts.owner_name} onAnnounce={announce} /> : null}
      <Items v={v} prefix={prefix} systemId={systemId} classed={!!v.class.current} />
      {v.bindings.length ? <Bindings v={v} title={title} names={names} /> : null}
      <Timeline systemId={systemId} title={title} prefix={prefix} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// KPIs + class
// ---------------------------------------------------------------------------

function SystemKpis({ v, slug, prefix, canDeclare, askDeclare, onAnnounce }: {
  v: View; slug: string; prefix: string; canDeclare: boolean; askDeclare: string | null; onAnnounce: (m: string) => void;
}) {
  const f = v.facts;
  const [open, setOpen] = useState(false);
  const focusAfter = useFocusAfter();
  const inferred = v.class.source === 'inferred' && v.class.current;
  const evidence = v.class.evidence[0]?.source;
  return (
    <>
      <KpiStrip label="Monitoring for this system">
        {!v.class.current ? (
          <KpiTile label="Class" id={CLASS_ANCHOR} tabIndex={-1} value={<StatusChip label="No class" tone="outline" glyph="circle" />}
            sub={<>Controls appear once set · <JumpLink to="mon-class-picker">Set class</JumpLink></>} />
        ) : inferred ? (
          <KpiTile label="Class" id={CLASS_ANCHOR} tabIndex={-1} value={<StatusChip label={`${classLabel(v.class.current)} · inferred`} tone="blue" glyph="unknown" />}
            sub={[`Inferred${evidence ? ` from ${evidence.replace(/_/g, ' ')}` : ''} · not confirmed`, !canDeclare ? askDeclare : null]}
            actions={canDeclare ? <>
              <Button compact onClick={() => setOpen(true)}>Confirm {classLabel(v.class.current)}</Button>
              <button type="button" className="gp-linkbtn gp-linkbtn--compact" onClick={() => setOpen(true)}>Change</button>
            </> : undefined} />
        ) : (
          <KpiTile label="Class" id={CLASS_ANCHOR} tabIndex={-1} value={classLabel(v.class.current)} valueSize="md"
            sub={[
              <span key="p" data-part="class-provenance">
                {[`Declared${v.class.declared_by_name ? ` by ${v.class.declared_by_name}` : ''}`, v.class.declared_at ? fmtDate(v.class.declared_at) : '',
                  v.class.review_by ? `re-review by ${fmtDate(v.class.review_by)}` : ''].filter(Boolean).join(' · ')}
                {v.class.inferred && v.class.inferred !== v.class.current ? ` · scan suggests ${classLabel(v.class.inferred)}` : ''}</span>,
              v.class.reason ? `“${v.class.reason}”` : null,
            ]}
            actions={canDeclare ? <button type="button" className="gp-linkbtn gp-linkbtn--compact" onClick={() => setOpen(true)}>Change</button> : undefined} />
        )}
        <KpiTile label="Signed pack" value={<PackChip state={f.pack_state} large />}
          sub={<>
            {f.pack_state === 'not_in_pack' ? 'In the next signed pack' : [carriedLine(f.carried), attestedLine(f.attested_pass, f.attested_of)].filter(Boolean).join(' · ') || 'Nothing shared yet'}
            {' · '}<Link to={`${prefix}/monitoring#mon-feeds-h`}>Shared feeds</Link>
          </>}>
          <ProgressLines row={f} />
        </KpiTile>
        {v.class.current ? (
          <KpiTile label="Controls passing" value={f.passing} of={`of ${f.of} passing`}
            sub={<>{f.covered} of {f.of} have a method · <JumpLink to="mon-controls">See controls</JumpLink>
              {v.coverage.buckets.excluded.length ? ` · ${v.coverage.buckets.excluded.length} excluded` : ''}</>} />
        ) : <KpiTile label="Controls passing" value="—" sub="Needs a class" />}
        <KpiTile label="Open findings" value={f.open_issues}
          sub={f.open_issues ? <Link to={monitoringFindingsHref(prefix, { system: v.system.system_id })}>Open findings</Link> : 'None open'} />
      </KpiStrip>
      <ClassDialog open={open} slug={slug} v={v} onClose={() => setOpen(false)} onDone={(m) => { setOpen(false); onAnnounce(m); focusAfter(CLASS_ANCHOR); }} />
    </>
  );
}

/** Today + 12 months as YYYY-MM-DD (the server's default re-review date). */
export function defaultReviewBy(now = new Date()): string {
  return isoDay(new Date(now.getFullYear() + 1, now.getMonth(), now.getDate()));
}
const isoDay = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const todayIso = () => isoDay(new Date());

/** Declare / confirm with the optional reason and re-review date (W-21); a past date answers 422, shown on the field. */
function useClassSubmit(systemId: string) {
  const declare = useDeclareSystemClass(systemId);
  const [reason, setReason] = useState('');
  const [reviewBy, setReviewBy] = useState(defaultReviewBy());
  const [reviewErr, setReviewErr] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reset = () => { setReason(''); setReviewBy(defaultReviewBy()); setReviewErr(null); setError(null); };
  const submit = async (choice: string): Promise<boolean> => {
    setError(null); setReviewErr(null);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(reviewBy)) { setReviewErr('Pick a date.'); return false; }
    try { await declare.mutateAsync({ system_class: choice as SystemClass, reason: reason.trim() || undefined, review_by: reviewBy }); return true; }
    catch (e) {
      const f = monitoringErrorOf(e);
      if (f.status === 422 && (f.fields.some((x) => x.path === 'review_by') || /review/i.test(f.message))) setReviewErr(f.fields.find((x) => x.path === 'review_by')?.message || f.message || 'Pick a date from today on.');
      else setError(f.message || 'The class could not be saved.');
      return false;
    }
  };
  return { declare, reason, setReason, reviewBy, setReviewBy, reviewErr, setReviewErr, error, reset, submit };
}

function ProvenanceFields({ uid, c }: { uid: string; c: ReturnType<typeof useClassSubmit> }) {
  return (
    <div className="mon-two">
      <Field id={`${uid}-reason`} label="Reason (optional)">
        <Input value={c.reason} placeholder="e.g. Calls tools on behalf of underwriters" onChange={(e) => c.setReason(e.currentTarget.value)} />
      </Field>
      <Field id={`${uid}-review`} label="Re-review by" required error={c.reviewErr ?? undefined}>
        <Input type="date" value={c.reviewBy} min={todayIso()} onChange={(e) => { c.setReviewBy(e.currentTarget.value); c.setReviewErr(null); }} />
      </Field>
    </div>
  );
}

function ClassDialog({ open, v, onClose, onDone }: { open: boolean; slug: string; v: View; onClose: () => void; onDone: (m: string) => void }) {
  const uid = useId();
  const c = useClassSubmit(v.system.system_id);
  const [choice, setChoice] = useState<string>(v.class.current ?? '');
  useEffect(() => { if (open) { setChoice(v.class.current ?? v.class.inferred ?? ''); c.reset(); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const same = choice === v.class.current;
  const confirming = same && v.class.source === 'inferred';
  const label = !choice ? 'Set class' : confirming ? `Confirm ${classLabel(choice)}` : same ? `Re-confirm ${classLabel(choice)}` : `Change to ${classLabel(choice)}`;
  return (
    <ActionDialog open={open} title="System class" submitLabel={label} onClose={onClose} busy={c.declare.isPending} error={c.error}
      submitDisabled={!choice}
      onSubmit={async () => { const ok = await c.submit(choice); if (ok) onDone(`Class ${confirming ? 'confirmed' : 'set'}: ${classLabel(choice)}`); return ok; }}>
      <fieldset className="mon-choices">
        <legend>Class</legend>
        {CLASS_CHOICES.map((x) => (
          <label key={x} className="mon-choice">
            <input type="radio" name={`${uid}-c`} value={x} checked={choice === x} onChange={() => setChoice(x)} />
            <span>{classLabel(x)}{v.class.inferred === x ? <span className="mon-sub"> · suggested</span> : null}</span>
          </label>
        ))}
      </fieldset>
      <ProvenanceFields uid={uid} c={c} />
      <p className="mon-hint">Controls follow the class; a change makes the signed pack stale until it is re-signed.</p>
    </ActionDialog>
  );
}

function ClassPicker({ v, canDeclare, ownerName, onDone }: { slug: string; v: View; canDeclare: { allowed: boolean; whoCan: string | null }; ownerName: string | null; onDone: (m: string) => void }) {
  const uid = useId();
  const c = useClassSubmit(v.system.system_id);
  const [choice, setChoice] = useState<string>(v.class.inferred ?? '');
  const [error, setError] = useState<string | null>(null);
  return (
    <Card dashed density="compact" id="mon-class-picker" tabIndex={-1} ariaLabel="Set the class">
      <h2 id={`${uid}-h`} className="mon-next-title">Set the class to see what this system needs</h2>
      {v.class.inferred ? <p className="mon-hint">Suggested: <strong>{classLabel(v.class.inferred)}</strong>{v.class.evidence[0] ? ` · from ${v.class.evidence[0].source.replace(/_/g, ' ')}` : ''}</p> : null}
      <fieldset className="mon-choices" disabled={!canDeclare.allowed}>
        <legend>Class</legend>
        {CLASS_CHOICES.map((c) => (
          <label key={c} className="mon-choice">
            <input type="radio" name={`${uid}-c`} value={c} checked={choice === c} onChange={() => setChoice(c)} />
            <span>{classLabel(c)}{v.class.inferred === c ? <span className="mon-sub"> · suggested</span> : null}</span>
          </label>
        ))}
      </fieldset>
      {canDeclare.allowed ? <ProvenanceFields uid={uid} c={c} /> : null}
      {error || c.error ? <Alert variant="error">{error ?? c.error}</Alert> : null}
      <div className="mon-actions">
        <GatedButton cap={canDeclare} ask={askText(canDeclare, { ownerName })} variant="primary" busy={c.declare.isPending} onClick={async () => {
          if (!choice) { setError('Pick a class.'); return; }
          setError(null);
          if (await c.submit(choice)) onDone(`Class set: ${classLabel(choice)}`);
        }}>{choice ? `Confirm ${classLabel(choice)}` : 'Set class'}</GatedButton>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Plan strip
// ---------------------------------------------------------------------------

function PlanStrip({ systemId, classConfirmed, freshness, latest, canSign, onAnnounce }: {
  slug: string; systemId: string; classConfirmed: boolean; freshness: View['plan_freshness'];
  latest: { package_id: string; version: number; signed_at: string } | null; canSign: { allowed: boolean; whoCan: string | null }; onAnnounce: (m: string) => void;
}) {
  const sign = useSignMonitoringPlan(systemId);
  const [error, setError] = useState<string | null>(null);
  const doSign = async () => {
    if (sign.isPending) return;
    setError(null);
    try { const out = await sign.mutateAsync(); onAnnounce(`Monitoring plan v${out.package.version} signed`); }
    catch (e) {
      const f = monitoringErrorOf(e);
      setError(f.kind === 'class_not_confirmed' ? 'Confirm the class first; the plan follows its prescription.' : f.kind === 'not_configured' ? 'Signing isn’t set up for this workspace yet.' : f.message || 'The plan could not be signed.');
    }
  };
  const label = latest && freshness.out_of_date ? 'Regenerate' : latest ? 'Sign new version' : 'Sign';
  return (
    <Card density="compact" id="mon-plan" tabIndex={-1} ariaLabel="Monitoring plan">
      <div className="mon-plan">
        <p className="mon-plan-main">
          <strong>Monitoring plan</strong>
          <span>{latest ? `· v${latest.version} signed ${fmtDate(latest.signed_at)}` : '· not signed'}</span>
          {latest && freshness.out_of_date ? <StatusChip label={`Out of date${freshness.since ? ` since ${fmtDate(freshness.since)}` : ''}`} tone="amber" glyph="clock" /> : null}
          {latest && !freshness.out_of_date ? <StatusChip label="Up to date" tone="ok" glyph="check" /> : null}
        </p>
        <div className="mon-actions">
          {latest ? <a className="gp-linkbtn gp-linkbtn--compact" href={`/verify/monitoring-plan/${encodeURIComponent(latest.package_id)}`} target="_blank" rel="noreferrer">Open plan<span className="gp-sr-only"> (opens in a new tab)</span></a> : null}
          {!classConfirmed ? (
            <span className="mon-cell-stack">
              <Button compact aria-disabled="true" aria-describedby="mon-plan-why" onClick={() => undefined}>{label}</Button>
              <JumpLink to={CLASS_ANCHOR}><span id="mon-plan-why">Confirm class first</span></JumpLink>
            </span>
          ) : (
            <GatedButton cap={canSign} ask="Ask an owner or admin" compact variant="secondary" busy={sign.isPending} onClick={doSign}>{label}</GatedButton>
          )}
        </div>
      </div>
      {error ? <p className="mon-err" role="alert">{error}</p> : null}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Controls
// ---------------------------------------------------------------------------

function Controls({ v, slug, prefix, title, validityCause, caps, names, onAnnounce }: {
  v: View; slug: string; prefix: string; title: (id: string) => string; validityCause: string | null;
  caps: ReturnType<typeof useMonitoringCapabilities>; names: ReadonlyMap<string, string>; onAnnounce: (m: string) => void;
}) {
  const qc = useQueryClient();
  const waiting = waitingKeys(v);
  const binding = (ctl: string, comp: string | null) => v.bindings.find((b) => b.control_id === ctl && (b.component_id ?? null) === comp) ?? null;
  const methodOf = (id: string | null | undefined) => (id ? v.methods.find((m) => m.method_id === id) ?? null : null);
  const cand = (ctl: string) => v.candidates.find((c) => c.control_id === ctl) ?? null;
  const next = v.facts.next_action;
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<Record<string, string>>({});
  const [step, setStep] = useState<StepSheetInput | null>(null);
  const cadence = (ctl: string) => cadenceLabel(v.prescription.find((p) => p.control_id === ctl)?.cadence_seconds ?? null);
  const excluded = v.coverage.buckets.excluded;
  // V114-5 (panel B1): reopening an N/A'd control is the N/A route's own gate (owner / admin).
  const { role } = useWorkspace();
  const reopen = useReopenControl();
  const reopenCap: Capability = role === 'owner' || role === 'admin' ? { allowed: true, whoCan: null } : { allowed: false, whoCan: whoCanText(['owner', 'admin']) };
  const changesHref = systemTabHref(prefix, v.system.system_id, CHANGES_ANCHOR);

  const reattest = async (ctl: CoverageControl) => {
    const stale = ctl.components.find((c) => c.reason === 'binding_not_fresh');
    const b = stale ? binding(ctl.control_id, stale.component_id) : null;
    if (!stale || !b?.method_id) return;
    setBusy(ctl.control_id);
    try {
      await verifyConfigurationItemMethod(slug, stale.component_id, b.method_id);
      await qc.invalidateQueries({ queryKey: monitoringKeys.root(slug) });
      onAnnounce(`${title(ctl.control_id)} re-attested`);
    } catch (e) { setErr((x) => ({ ...x, [ctl.control_id]: monitoringErrorOf(e).message || 'Could not re-attest.' })); }
    finally { setBusy(null); }
  };
  // "Run all checks now" is the workspace sweep, once per page (card header); a toast says when results arrive.
  const runAll = useRunAllChecks(slug);

  return (
    <Card density="compact" id="mon-controls" tabIndex={-1} headingId="mon-controls-h"
      title={`Controls for ${classPhrase(v.class.current)} system`}
      right={
        <div className="mon-actions">
          <p className="mon-card-note">Passes only when every linked configuration item passes{excluded.length ? ` · ${excluded.length} excluded` : ''}</p>
          <Button compact aria-disabled={runAll.busy || undefined} onClick={() => void runAll.run()}>{runAll.busy ? 'Starting…' : 'Run all checks now'}</Button>
        </div>}>
      {runAll.error ? <p className="mon-err" role="alert">{runAll.error}</p> : null}
      {!v.coverage.controls.length ? (
        <EmptyState title="Nothing prescribed yet" description="The class has no approved prescription yet."
          action={<Link className="gp-linkbtn" to={`${prefix}/monitoring`}>Back to Monitoring</Link>} />
      ) : (
        <ul className="mon-ctls">
          {v.coverage.controls.map((ctl) => {
            const st = CTL_STATUS[ctl.reason];
            const waits = [...waiting].some((k) => k.startsWith(`${ctl.control_id}|`));
            const unmonCount = ctl.components.filter((c) => c.status !== 'covered' && c.reason !== 'last_check_not_passing' && c.reason !== 'binding_not_fresh').length;
            const failingCell = ctl.components.find((c) => c.reason === 'last_check_not_passing');
            const failingLatest = failingCell ? binding(ctl.control_id, failingCell.component_id)?.latest ?? null : null;
            const staleCell = ctl.components.find((c) => c.reason === 'binding_not_fresh');
            const staleBinding = staleCell ? binding(ctl.control_id, staleCell.component_id) : null;
            const staleMethod = methodOf(staleBinding?.method_id);
            const firstUnmon = ctl.components.find((c) => c.status !== 'covered' && c !== failingCell && c !== staleCell);
            const c = cand(ctl.control_id);
            const findingId = next?.target.control_id === ctl.control_id ? next.target.finding_id ?? null : null;
            const fixLabel = next?.kind === 'fix_failing' && next.target.control_id === ctl.control_id ? next.label : `Fix ${title(ctl.control_id)}`;
            const firstB = ctl.components.map((x) => binding(ctl.control_id, x.component_id)).find(Boolean) ?? null;
            const chip = waits && !ctl.covered ? { label: 'Waiting for first run', tone: 'blue' as const, glyph: 'dots' as const }
              : ctl.reason === 'unmonitored_ci' ? { ...st, label: `Unmonitored on ${plural(unmonCount || 1, 'configuration item')}` } : st;
            const cause = failingLatest?.cause ?? (next?.target.control_id === ctl.control_id ? next.cause : null);
            return (
              <li key={ctl.control_id} id={controlAnchor(ctl.control_id)} tabIndex={-1} className={`mon-ctl${ctl.reason === 'last_check_not_passing' ? ' mon-ctl--fail' : ''}`}>
                <div className="mon-ctl-body">
                  <h3 className="mon-ctl-title">{title(ctl.control_id)} <StatusChip label={chip.label} tone={chip.tone} glyph={chip.glyph} /></h3>
                  <p className="mon-line">
                    <span>{firstB ? `${detectorLabel(firstB.detector_kind, firstB.detector_ref)}${cadence(ctl.control_id) ? ` · ${cadence(ctl.control_id)}` : ''}` : 'No method yet'}</span>
                    {ctl.reason === 'last_check_not_passing' && v.facts.sla_due ? <span>Due {fmtDate(v.facts.sla_due)}</span> : null}
                    {findingId ? <Link to={findingHref(prefix, findingId)}>Open finding</Link> : null}
                  </p>
                  {failingCell ? <Observation latest={failingLatest} context={title(ctl.control_id)} /> : null}
                  {failingCell && cause ? <CauseLine cause={cause} seeHref={changesHref} />
                    : ctl.reason === 'last_check_not_passing' && validityCause ? <p className="mon-line" data-part="cause"><StatusGlyph kind="dots" size={12} /> {validityCause}</p> : null}
                  {ctl.components.length ? (
                    <div className="mon-cells" role="list" aria-label={`${title(ctl.control_id)}: passes when every item passes`}>
                      {ctl.components.map((comp, i) => {
                        const b = binding(ctl.control_id, comp.component_id);
                        const s = cellState(comp.reason, waiting.has(`${ctl.control_id}|${comp.component_id}`));
                        const attested = b?.detector_kind === 'attestation';
                        const by = attested ? (b?.accepted_by ? names.get(b.accepted_by) : null) ?? (b?.owner_user_id ? names.get(b.owner_user_id) : null) : null;
                        return (
                          <span key={comp.component_id} className="mon-cells" role="listitem">
                            {i ? <span className="mon-and" aria-hidden="true">and</span> : null}
                            <span className={`mon-cell${s === 'fail' ? ' mon-cell--fail' : s === 'unmon' || s === 'gone' ? ' mon-cell--unmon' : s === 'wait' ? ' mon-cell--wait' : ''}`}>
                              <StatusGlyph kind={CELL_GLYPH[s]} size={12} />
                              <Link to={ciHref(prefix, comp.component_id, v.system.system_id)}>{comp.ci_name}</Link>
                              <span className={s === 'pass' || s === 'fail' ? 'gp-sr-only' : undefined}>{CELL_WORD[s]}</span>
                              {b ? <span className="mon-cell-mark">{attested ? `attested${by ? ` · ${by}` : ''}` : 'auto'}</span> : null}
                            </span>
                          </span>
                        );
                      })}
                    </div>
                  ) : null}
                  {staleCell && staleBinding ? (
                    <div className="mon-fix">
                      {/* Panel medium: one staleness word per row — this block agrees with the row chip ("Past cadence"). */}
                      <span className="mon-line mon-line--text"><StatusChip label={`Past cadence since ${fmtDate(staleBinding.freshness_expires_at)}`} tone="amber" glyph="clock" />
                        <span>{staleBinding.detector_kind === 'attestation' ? 're-attest due now' : staleBinding.detector_kind === 'ci_parser' ? 're-run your pipeline' : 'next check overdue'}</span></span>
                      <span className="mon-sub">{detectorLabel(staleBinding.detector_kind, staleBinding.detector_ref)}
                        {staleMethod?.last_run_at ? ` · last run ${fmtDate(staleMethod.last_run_at)}` : ` · last pass ${fmtDate(staleBinding.last_pass_at)}`}
                        {staleBinding.cadence_seconds ? ` · ${cadenceLabel(staleBinding.cadence_seconds)}` : ''}
                        {staleMethod?.last_run_ref ? <> · <a href={staleMethod.last_run_ref} target="_blank" rel="noreferrer">Open last run<span className="gp-sr-only"> (opens in a new tab)</span></a></> : null}</span>
                    </div>
                  ) : null}
                  {err[ctl.control_id] ? <p className="mon-err" role="alert">{err[ctl.control_id]}</p> : null}
                </div>
                <div className="mon-ctl-acts">
                  {failingCell ? (
                    <Link className="gp-linkbtn gp-linkbtn--compact btn-primary" to={ciHref(prefix, failingCell.component_id, v.system.system_id)}>{fixLabel}</Link>
                  ) : staleCell && staleBinding?.detector_kind === 'attestation' ? (
                    <GatedButton cap={caps.verify_method} ask={askText(caps.verify_method, { ownerName: v.facts.owner_name })} compact busy={busy === ctl.control_id} onClick={() => void reattest(ctl)}>Re-attest</GatedButton>
                  ) : staleCell && staleBinding?.detector_kind === 'ci_parser' && staleMethod && (staleMethod.setup || staleMethod.setup_unavailable_reason) ? (
                    <Button compact onClick={() => setStep({
                      name: detectorLabel(staleMethod.detector_kind, staleMethod.detector_ref), controlTitle: title(ctl.control_id), componentName: staleCell.ci_name,
                      setup: staleMethod.setup, unavailable: staleMethod.setup_unavailable_reason, returnTo: controlAnchor(ctl.control_id),
                    })}>Show the step</Button>
                  ) : staleCell ? (
                    <Button compact aria-disabled={runAll.busy || undefined} onClick={() => void runAll.run()}>Run all checks now</Button>
                  ) : waits && !ctl.covered ? null
                    : c ? <JumpLink className="gp-linkbtn gp-linkbtn--compact" to={`mon-cand-${c.candidate_id}`}>Tool available: {detectorLabel(c.detector_kind, c.detector_ref)}</JumpLink>
                      : firstUnmon ? <Link className="gp-linkbtn gp-linkbtn--compact" to={ciHref(prefix, firstUnmon.component_id, v.system.system_id)}>Add a method</Link>
                        : !ctl.covered ? <Link className="gp-linkbtn gp-linkbtn--compact" to={`${prefix}/setup`}>Connect a source</Link> : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {runAll.toast}
      <StepSheet input={step} prefix={prefix} onClose={() => setStep(null)} />
      {excluded.length ? (
        <details className="mon-disclose" data-part="excluded">
          <summary>{excluded.length} excluded</summary>
          <ul className="mon-rows">
            {excluded.map((x) => (
              <li key={x.control_id} className="mon-row" id={`mon-excl-${x.control_id}`} tabIndex={-1}>
                <span className="mon-row-main"><span className="mon-name">{title(x.control_id)}</span>
                  <span className="mon-sub">{metaLine(['Not applicable', x.rationale, x.who ? names.get(x.who) : null, x.when ? fmtDate(x.when) : null])}</span></span>
                {/* V114-5 (panel B1): reopening clears the N/A — the inverse record, same owner/admin gate. */}
                <span className="mon-row-acts">
                  <GatedButton cap={reopenCap} ask={reopenCap.whoCan} compact busy={reopen.isPending && reopen.variables?.controlId === x.control_id}
                    onClick={async () => {
                      setErr((e) => ({ ...e, [`na-${x.control_id}`]: '' }));
                      try {
                        await reopen.mutateAsync({ systemId: v.system.system_id, controlId: x.control_id });
                        onAnnounce(`${title(x.control_id)} reopened · back in the controls`);
                      } catch (e) { setErr((m) => ({ ...m, [`na-${x.control_id}`]: monitoringErrorOf(e).message || 'Could not reopen.' })); }
                    }}>Reopen<span className="gp-sr-only"> · {title(x.control_id)}</span></GatedButton>
                </span>
                {err[`na-${x.control_id}`] ? <p className="mon-err" role="alert">{err[`na-${x.control_id}`]}</p> : null}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      {/* V114-5-3: out-of-scope items beside the coverage reading — a disclosure listing, not primary chrome. */}
      {v.coverage.buckets.out_of_scope.length ? (
        <details className="mon-disclose" data-part="out-of-scope">
          <summary>{v.coverage.buckets.out_of_scope.length} out of scope</summary>
          <ul className="mon-rows">
            {v.coverage.buckets.out_of_scope.map((x) => (
              <li key={x.component_id} className="mon-row"><span className="mon-row-main">
                <Link className="mon-name" to={ciHref(prefix, x.component_id, v.system.system_id)}>{x.ci_name}</Link>
                <span className="mon-sub">{metaLine(['Out of scope', x.reason, x.decided_by, x.decided_at ? fmtDate(x.decided_at) : null])}</span></span></li>
            ))}
          </ul>
        </details>
      ) : null}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Candidates
// ---------------------------------------------------------------------------

function Candidates({ v, slug: _slug, prefix, title, canAccept, ownerName, onAnnounce }: {
  v: View; slug: string; prefix: string; title: (id: string) => string; canAccept: { allowed: boolean; whoCan: string | null }; ownerName: string | null; onAnnounce: (m: string) => void;
}) {
  const accept = useAcceptCandidate(v.system.system_id);
  const [setup, setSetup] = useState<Candidate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const focusAfter = useFocusAfter();
  const list = v.candidates.filter((c) => !c.unplaced);
  const doAccept = async (c: Candidate) => {
    setError(null);
    try {
      await accept.mutateAsync(c.candidate_id);
      onAnnounce(`${detectorLabel(c.detector_kind, c.detector_ref)} accepted · waiting for its first run`);
      return true;
    } catch (e) {
      const f = monitoringErrorOf(e);
      setError(f.kind === 'not_found' ? 'That proposal changed. The list is refreshed.' : f.message || 'Could not accept.');
      return false;
    }
  };
  const covered = new Set(list.map((c) => c.control_id)).size;
  return (
    <Card density="compact" id="mon-candidates" headingId="mon-cand-h" title="Your tools that can cover this"
      right={list.length ? <p className="mon-card-note">{list.length} to accept · covers {plural(covered, 'control')}</p> : undefined}>
      {error ? <Alert variant="error">{error}</Alert> : null}
      {!list.length ? (
        <EmptyState title="No tool found for the open controls" description="Connect a source so its guardrails, gateways and monitors can be proposed."
          action={<Link className="gp-linkbtn" to={`${prefix}/setup`}>Connect a source</Link>} />
      ) : (
        <ul className="mon-rows">
          {list.map((c) => {
            const label = detectorLabel(c.detector_kind, c.detector_ref);
            const needsSetup = !!c.setup || !!c.setup_unavailable_reason;
            const act = c.setup ? 'Accept and set up' : c.setup_unavailable_reason ? 'Accept and attest' : c.fit === 'partial' ? 'Accept as partial' : c.fit === 'substitute' ? 'Accept as substitute' : `Accept ${label}`;
            return (
              <li key={c.candidate_id} id={`mon-cand-${c.candidate_id}`} tabIndex={-1} className="mon-row">
                <span className="mon-row-main">
                  <span className="mon-name">{label}</span>
                  <span className="mon-line">
                    <span>For <JumpLink to={controlAnchor(c.control_id)}>{title(c.control_id)}</JumpLink></span>
                    <StatusChip label={c.setup ? `${fitLabel(c.fit)} · needs a pipeline step` : c.setup_unavailable_reason ? `${fitLabel(c.fit)} · attested by a member` : fitLabel(c.fit)} tone={c.fit === 'native' ? 'ok' : 'muted'} />
                    {c.component_name ? <span>{c.component_name} · {kindLabel(c.component_kind)}</span> : null}
                    <span>found by {c.source.replace(/_/g, ' ')}</span>
                  </span>
                </span>
                <span className="mon-row-acts">
                  <GatedButton cap={canAccept} ask={askText(canAccept, { ownerName })} compact busy={accept.isPending && accept.variables === c.candidate_id}
                    onClick={async () => { if (needsSetup) setSetup(c); else if (await doAccept(c)) focusAfter(controlAnchor(c.control_id)); }}>{act}<span className="gp-sr-only"> for {title(c.control_id)}</span></GatedButton>
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <StepSheet prefix={prefix} busy={accept.isPending} onClose={() => setSetup(null)}
        input={setup ? { name: detectorLabel(setup.detector_kind, setup.detector_ref), controlTitle: title(setup.control_id), componentName: setup.component_name, setup: setup.setup, unavailable: setup.setup_unavailable_reason, returnTo: controlAnchor(setup.control_id) } : null}
        onAccept={async () => {
          if (!setup) return false;
          const target = controlAnchor(setup.control_id);
          const ok = await doAccept(setup);
          // U-8: close first, then focus the control (focusing while the modal is open is a no-op on the inert page).
          if (ok) { setSetup(null); focusAfter(target); }
          return ok;
        }} />
    </Card>
  );
}

type Candidate = View['candidates'][number];
type PipelineSetup = Candidate['setup'];

export interface StepSheetInput {
  name: string; controlTitle: string; componentName: string | null; setup: PipelineSetup; unavailable: string | null; returnTo?: string;
}

/**
 * The pipeline step (`setup`): "GitHub Actions" (a `run:` step) or "Other pipelines" (plain shell), each copied from
 * data-value, with how to produce the report file and the runner docs. Two uses: accepting a candidate (`onAccept`),
 * and "Show the step" for a stale pipeline method (B-3, read-only: Close). With no snippet the reason shows and the
 * method is attested instead. The tabs follow the tab keyboard model (arrows, Home / End).
 */
export function StepSheet({ input, busy = false, prefix, onClose, onAccept }: {
  input: StepSheetInput | null; busy?: boolean; prefix: string; onClose: () => void; onAccept?: () => Promise<boolean>;
}) {
  const uid = useId();
  const [tab, setTab] = useState<'github' | 'other'>('github');
  useEffect(() => { if (input) setTab('github'); }, [input?.name, input?.controlTitle]); // eslint-disable-line react-hooks/exhaustive-deps
  const s = input?.setup ?? null;
  const accepting = !!onAccept;
  const TABS = [['github', 'GitHub Actions'], ['other', 'Other pipelines']] as const;
  const onKey = (e: KeyboardEvent, i: number) => {
    const n = e.key === 'ArrowRight' ? (i + 1) % 2 : e.key === 'ArrowLeft' ? (i + 1) % 2 : e.key === 'Home' ? 0 : e.key === 'End' ? 1 : -1;
    if (n < 0) return;
    e.preventDefault();
    setTab(TABS[n]![0]);
    document.getElementById(`${uid}-t-${TABS[n]![0]}`)?.focus();
  };
  return (
    <ActionDialog open={!!input} busy={busy} onClose={onClose}
      title={!input ? '' : !accepting ? `Re-run ${input.name}` : s ? `Accept and set up ${input.name}` : `Accept ${input.name}`}
      submitLabel={s ? 'Accept' : 'Accept and attest'} hideSubmit={!accepting} cancelLabel={accepting ? 'Cancel' : 'Close'}
      onSubmit={onAccept ?? (() => true)}>
      {input ? (
        <>
          <dl className="mon-dl">
            <dt>Control</dt><dd>{input.controlTitle}</dd>
            <dt>Configuration item</dt><dd>{input.componentName ?? '—'}</dd>
          </dl>
          {s ? (
            <>
              <div className="mon-actions" role="tablist" aria-label="Where your pipeline runs">
                {TABS.map(([k, l], i) => (
                  <Button key={k} compact role="tab" id={`${uid}-t-${k}`} aria-selected={tab === k} aria-controls={`${uid}-p`} tabIndex={tab === k ? 0 : -1}
                    variant={tab === k ? 'secondary' : 'ghost'} onKeyDown={(e) => onKey(e, i)} onClick={() => setTab(k)}>{l}</Button>
                ))}
              </div>
              <div id={`${uid}-p`} role="tabpanel" aria-labelledby={`${uid}-t-${tab}`} className="mon-col">
                {tab === 'github'
                  ? <CopyField key="gh" label="Add this step to your workflow" value={s.snippet} />
                  : <CopyField key="cli" label="Run this in your pipeline" value={s.cli_snippet} />}
              </div>
              <p className="mon-hint">{s.produce}</p>
              {s.docs_href ? <p className="mon-hint"><a href={s.docs_href} target="_blank" rel="noreferrer">Runner release notes<span className="gp-sr-only"> (opens in a new tab)</span></a></p> : null}
              <p className="mon-hint">{accepting ? 'Shows “Waiting for first run” until the step reports' : 'The control passes again once the next run reports'}</p>
            </>
          ) : (
            <>
              <p className="mon-hint" data-part="setup-unavailable">{input.unavailable}</p>
              <p className="mon-hint">{accepting ? 'Accept it and a member attests it on the configuration item page, or ' : 'A member attests it on the configuration item page, or '}<Link to={`${prefix}/setup`}>connect a source</Link> that reads it.</p>
            </>
          )}
        </>
      ) : null}
    </ActionDialog>
  );
}

// ---------------------------------------------------------------------------
// Configuration items, bindings, timeline
// ---------------------------------------------------------------------------

function Items({ v, prefix, systemId, classed }: { v: View; prefix: string; systemId: string; classed: boolean }) {
  const failing = new Set(v.coverage.controls.flatMap((c) => c.components.filter((x) => x.reason === 'last_check_not_passing').map((x) => x.component_id)));
  const stale = new Set(v.coverage.controls.flatMap((c) => c.components.filter((x) => x.reason === 'binding_not_fresh').map((x) => x.component_id)));
  const unmon = new Set(v.coverage.buckets.unmonitored_components.map((x) => x.component_id));
  const pending = new Map(v.coverage.buckets.pending_review.map((x) => [x.component_id, x.pending_until]));
  const scoped = new Map(v.coverage.buckets.out_of_scope.map((x) => [x.component_id, x]));
  const counted = v.components.filter((c) => !scoped.has(c.component_id));
  return (
    <Card density="compact" id="mon-items" tabIndex={-1} headingId="mon-items-h" title="Configuration items"
      right={<p className="mon-card-note">{counted.length}{unmon.size ? ` · ${unmon.size} unmonitored` : ''}</p>}>
      {!v.components.length ? (
        <EmptyState title="No configuration items yet" description="Items arrive from the BOM, the boundary and connected sources."
          action={<Link className="gp-linkbtn" to={`${prefix}/setup`}>Connect a source</Link>} />
      ) : (
        <ul className="mon-rows">
          {v.components.map((c) => {
            const href = ciHref(prefix, c.component_id, systemId);
            const oos = scoped.get(c.component_id);
            const state = c.gone_at ? { label: 'Gone', tone: 'outline' as const, glyph: 'circle' as const }
              : oos ? { label: 'Out of scope', tone: 'outline' as const, glyph: 'circle' as const }
                : failing.has(c.component_id) ? { label: 'Stopped passing', tone: 'outline' as const, glyph: 'x' as const }
                  : stale.has(c.component_id) ? { label: 'Past cadence', tone: 'amber' as const, glyph: 'clock' as const }
                    : pending.has(c.component_id) ? { label: 'In review', tone: 'muted' as const, glyph: 'clock' as const }
                      : unmon.has(c.component_id) || !classed ? { label: 'Unmonitored', tone: 'outline' as const, glyph: 'circle' as const }
                        : { label: 'Passing', tone: 'ok' as const, glyph: 'check' as const };
            const action = !classed ? <JumpLink className="gp-linkbtn gp-linkbtn--compact" to="mon-class-picker">Set class first</JumpLink>
              : c.gone_at ? <Link className="gp-linkbtn gp-linkbtn--compact" to={href}>Retire or relink</Link>
                : failing.has(c.component_id) || stale.has(c.component_id) ? <Link className="gp-linkbtn gp-linkbtn--compact" to={href}>See control</Link>
                  : oos ? <Link className="gp-linkbtn gp-linkbtn--compact" to={href}>Bring into scope</Link>
                    : pending.has(c.component_id) ? <Link className="gp-linkbtn gp-linkbtn--compact" to={href}>Review</Link>
                      : unmon.has(c.component_id) ? <Link className="gp-linkbtn gp-linkbtn--compact" to={href}>Add a method</Link> : null;
            return (
              <li key={c.component_id} className="mon-row">
                <span className="mon-row-main">
                  <Link className="mon-name" to={href}>{c.ci_name}</Link>
                  <span className="mon-sub">{metaLine([
                    kindLabel(c.kind), envLabel(c.environment),
                    ...(oos ? ['Out of scope', oos.reason, oos.decided_by, oos.decided_at ? fmtDate(oos.decided_at) : null] : []),
                    pending.get(c.component_id) ? `in review until ${fmtDate(pending.get(c.component_id))}` : null,
                  ])}</span>
                </span>
                <span className="mon-row-acts"><StatusChip label={state.label} tone={state.tone} glyph={state.glyph} />{action}</span>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function Bindings({ v, title, names }: { v: View; title: (id: string) => string; names: ReadonlyMap<string, string> }) {
  const ci = new Map(v.components.map((c) => [c.component_id, c.ci_name]));
  return (
    <Card density="compact" headingId="mon-bind-h" title="Bindings" right={<p className="mon-card-note">One per control and configuration item</p>}>
      <details className="mon-disclose">
        <summary>Show {plural(v.bindings.length, 'binding')}</summary>
        <ul className="mon-rows">
          {v.bindings.map((b) => (
            <li key={b.binding_id} className="mon-row">
              <span className="mon-row-main">
                <span className="mon-name">{title(b.control_id)} · {b.component_id ? ci.get(b.component_id) ?? 'item' : 'workspace'}</span>
                <span className="mon-sub">{detectorLabel(b.detector_kind, b.detector_ref)}{b.cadence_seconds ? ` · ${cadenceLabel(b.cadence_seconds)}` : ''}
                  {b.owner_user_id && names.get(b.owner_user_id) ? ` · Owner ${names.get(b.owner_user_id)}` : ''} · last pass {fmtDate(b.last_pass_at)}</span>
                <Observation latest={b.latest} hideVerify />
                <VerifyDisclosure context={`${title(b.control_id)} · ${b.component_id ? ci.get(b.component_id) ?? 'item' : 'workspace'}`}>
                  <dl className="mon-dl">
                    {b.latest?.observed_raw ? <><dt>Recorded</dt><dd className="mon-mono">{b.latest.observed_raw}</dd></> : null}
                    {b.latest?.evidence_ref ? <><dt>Evidence</dt><dd className="mon-mono">{b.latest.evidence_ref}</dd></> : null}
                    <dt>Binding</dt><dd className="mon-mono">{b.binding_id}</dd>
                    <dt>Method</dt><dd className="mon-mono">{b.method_id ?? '—'}</dd>
                    <dt>Last check</dt><dd className="mon-mono">{b.last_check_id ?? '—'}</dd>
                  </dl>
                </VerifyDisclosure>
              </span>
              <span className="mon-row-acts"><StatusChip label={b.binding_status === 'active' ? 'Active' : b.binding_status === 'stale' ? 'Stale' : 'Unbound'} tone={b.binding_status === 'active' ? 'ok' : 'amber'} /></span>
            </li>
          ))}
        </ul>
      </details>
    </Card>
  );
}

function tlTitle(e: TimelineEntry, title: (id: string) => string): { text: string; glyph: 'x' | 'check' | 'dots' | 'circle'; tone: 'bad' | 'good' | '' } {
  if (e.transition === 'degraded') return { text: `Stopped passing: ${title(e.control_id)}`, glyph: 'x', tone: 'bad' };
  if (e.transition === 'recovered') return { text: `Started passing: ${title(e.control_id)}`, glyph: 'check', tone: 'good' };
  if (e.transition === 'first') return { text: `First result: ${title(e.control_id)}`, glyph: e.status === 'pass' ? 'check' : 'x', tone: e.status === 'pass' ? 'good' : 'bad' };
  return { text: `Config changed: ${e.ci_name || title(e.control_id)}`, glyph: 'dots', tone: '' };
}

function Timeline({ systemId, title, prefix }: { systemId: string; title: (id: string) => string; prefix: string }) {
  const tl = useMonitoringTimeline(systemId, { only: 'changes' });
  return (
    <Card density="compact" id="mon-changes" tabIndex={-1} headingId="mon-tl-h" title="What changed"
      right={<p className="mon-card-note">Last 30 days · <Link to={`${prefix}/audit`}>Full audit trail</Link></p>}>
      {tl.loading ? <Spinner label="Loading changes" /> : tl.error ? (
        <Alert variant="error">Changes could not be loaded. <Button compact onClick={() => void tl.refetch()}>Retry</Button></Alert>
      ) : !tl.entries.length ? <p className="mon-hint">No changes in the last 30 days.</p> : (
        <>
          <ol className="mon-timeline">
            {tl.entries.map((e) => {
              const t = tlTitle(e, title);
              return (
                <li key={e.history_id} className="mon-tl">
                  <span className="mon-tl-when">{fmtDate(e.observed_at)}</span>
                  <span className={`mon-tl-glyph${t.tone ? ` mon-tl-glyph--${t.tone}` : ''}`} aria-hidden="true"><StatusGlyph kind={t.glyph} size={14} /></span>
                  <span className="mon-row-main">
                    <p className="mon-tl-title">{t.text}</p>
                    <span className="mon-line">{e.ci_name ? <span>{e.ci_name}</span> : null}
                      {e.config_changed && e.transition !== 'unchanged' ? <span>after a config change</span> : null}
                      {e.finding_id ? <Link to={findingHref(prefix, e.finding_id)}>Open finding</Link> : null}</span>
                  </span>
                </li>
              );
            })}
          </ol>
          {tl.hasMore ? <div className="mon-actions"><Button compact aria-disabled={tl.loadingMore || undefined} onClick={() => { if (!tl.loadingMore) void tl.loadMore(); }}>{tl.loadingMore ? 'Loading…' : 'Show older'}</Button></div> : null}
        </>
      )}
    </Card>
  );
}
